import { useState } from 'react';
import { Button, Form, InputNumber, Radio } from 'antd';
import type { FormInstance } from 'antd';
import {
  conversionBonusFor,
  conversionTierFor,
  conversionTierRangeLabel,
  normalizeConversionTiers,
  type ConversionBonusTier,
} from '@salaries/shared';

// בונוס המרה מניסיון — ₪ per trial lead who converted to a member within the
// window, at a rate picked by HOW MANY conversions the coach had that period.
// Editor for the ladder plus (at box level) the on/off radio + the window,
// shared by the box defaults and the per-staff "custom" section so the inputs
// stay identical.
//
// NOT marginal: only the SINGLE tier the period's count reaches applies — and
// it applies to EVERY conversion, not just the ones past the threshold (5
// conversions on "1→₪50, 5→₪70" pay 5×70, not 4×50+70). The calculator at the
// bottom spells that out with real numbers.

/** Range label per tier, computed off the SORTED, deduped ladder so the rows can
 *  stay in whatever order they were typed. */
function rangeLabels(tiers: ConversionBonusTier[]): Map<number, string> {
  const sorted = normalizeConversionTiers(tiers);
  const map = new Map<number, string>();
  sorted.forEach((t, i) => {
    const next = sorted[i + 1];
    map.set(
      t.from_count,
      conversionTierRangeLabel({
        from_count: t.from_count,
        to_count: next ? next.from_count - 1 : null,
      }),
    );
  });
  return map;
}

export function ConversionTiersInput({
  value = [],
  onChange,
}: {
  value?: ConversionBonusTier[];
  onChange?: (v: ConversionBonusTier[]) => void;
}) {
  const rows = value ?? [];
  const commit = (next: ConversionBonusTier[]) => onChange?.(next);
  const patch = (i: number, p: Partial<ConversionBonusTier>) =>
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const add = () => {
    // Start the new tier one conversion above the highest one so far — the
    // common case is "…and from there on up".
    const nextFrom = rows.length ? Math.max(...rows.map((r) => r.from_count || 1)) + 1 : 1;
    commit([...rows, { from_count: nextFrom, amount: 0 }]);
  };

  const labels = rangeLabels(rows);
  // Two rows starting at the same count: only one of them can survive the save
  // (the last one entered wins), so say so instead of silently dropping money.
  const dup = rows.length !== new Set(rows.map((r) => r.from_count)).size;
  // A ladder that doesn't start at 1 pays nothing below its first tier — worth
  // saying, it's the easy one to miss.
  const lowest = rows.length ? Math.min(...rows.map((r) => r.from_count || 1)) : 1;

  const topFrom = rows.length ? Math.max(...rows.map((r) => r.from_count || 1)) : 0;
  const [sample, setSample] = useState<number | null>(null);
  const sampleCount = sample ?? Math.max(1, topFrom);
  const sampleTier = conversionTierFor(sampleCount, rows);
  const sampleTotal = conversionBonusFor(sampleCount, rows);

  return (
    <div>
      {rows.length > 0 && (
        <div className="border rounded mb-2">
          {rows.map((r, i) => (
            // Two fixed lines — inputs, then the range label + remove — so every
            // row wraps the same way in the narrow drawer.
            <div key={i} className="px-3 py-2 border-b last:border-b-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-secondary-text">מ-</span>
                <InputNumber
                  min={1}
                  max={1000}
                  value={r.from_count}
                  onChange={(v) =>
                    patch(i, { from_count: Math.max(1, Math.floor(Number(v) || 1)) })
                  }
                  dir="ltr"
                  style={{ width: 70 }}
                />
                <span className="text-secondary-text">המרות,</span>
                <InputNumber
                  min={0}
                  suffix="₪"
                  value={r.amount}
                  onChange={(v) => patch(i, { amount: Math.max(0, Number(v) || 0) })}
                  dir="ltr"
                  style={{ width: 110 }}
                />
                <span className="text-secondary-text">לכל המרה</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-small text-secondary-text">{labels.get(r.from_count)}</span>
                <span className="flex-1" />
                <Button size="small" danger type="text" onClick={() => remove(i)}>
                  הסר
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button onClick={add} disabled={rows.length >= 20}>
        הוסף מדרגה
      </Button>

      {dup && (
        <div className="text-small mt-2" style={{ color: '#d4380d' }}>
          שתי מדרגות מתחילות מאותו מספר המרות — שימו לב כי רק האחרונה תישמר.
        </div>
      )}
      {rows.length > 0 && lowest > 1 && (
        <div className="text-small mt-2 text-secondary-text">
          איש צוות עם פחות מ-{lowest} המרות בתקופה לא יקבל בונוס המרה.
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-3 p-3 rounded bg-grey-bg">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-secondary-text">בדיקה: איש צוות עם</span>
            <InputNumber
              min={1}
              max={1000}
              value={sampleCount}
              onChange={(v) => setSample(Math.max(1, Math.floor(Number(v) || 1)))}
              dir="ltr"
              style={{ width: 80 }}
            />
            <span className="text-secondary-text">המרות בתקופה יקבל</span>
            <span className="font-semibold">₪{sampleTotal.toLocaleString()}</span>
          </div>
          <div className="text-small text-secondary-text mt-1">
            {!sampleTier
              ? 'לא נכנס לאף מדרגה — ללא בונוס'
              : sampleTotal > 0
                ? `${sampleCount}×${sampleTier.amount}₪ (${labels.get(sampleTier.from_count)}) — כל ההמרות בתקופה מקבלות את הסכום של המדרגה שאליה הגיע`
                : `המדרגה (${labels.get(sampleTier.from_count)}) מוגדרת ללא סכום — ללא בונוס`}
          </div>
        </div>
      )}
    </div>
  );
}

// Box defaults: on/off + the window (both box-wide) + the default ladder. The
// ladder stays MOUNTED while off so switching it back on doesn't come back empty
// (and antd doesn't drop it on save).
export function ConversionFields({ form }: { form: FormInstance }) {
  const enabled = Form.useWatch('conversion_enabled', form);
  return (
    <>
      <Form.Item name="conversion_enabled" className="mb-0">
        <Radio.Group className="flex flex-col gap-3">
          <Radio value={false}>ללא בונוס המרה</Radio>
          <Radio value={true}>בונוס לכל המרה לפי כמות ההמרות בתקופה</Radio>
        </Radio.Group>
      </Form.Item>
      <div style={{ display: enabled ? undefined : 'none' }} className="mt-3">
        <Form.Item name="conversion_window_days" label="חלון ימים מהשיעור להמרה" className="mb-3">
          <InputNumber min={0} max={365} style={{ width: 160 }} dir="ltr" />
        </Form.Item>
        <Form.Item name="conversion_tiers" className="mb-0">
          <ConversionTiersInput />
        </Form.Item>
      </div>
    </>
  );
}

// Per-staff "custom" ladder — the source select already drives on/off, and the
// window is box-wide, so only the ladder shows.
export function ConversionTiersField() {
  return (
    <Form.Item name="conversion_tiers" className="mt-3 mb-0">
      <ConversionTiersInput />
    </Form.Item>
  );
}
